import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import "./booking-popup.css";

// The scheduler iframe is the expensive part, so it is only fetched once the
// dialog actually opens. Before that the popup costs a timer and nothing else.
const BrandedScheduler = lazy(() =>
  import("@/components/BrandedScheduler").then((m) => ({
    default: m.BrandedScheduler,
  })),
);

export const DISMISS_KEY = "chleb:booking-popup:dismissed";
export const OPEN_DELAY = 60_000;
const BLOCKED = [
  "/book",
  "/contact",
  "/privacy-policy",
  "/terms-of-service",
  "/cookie-policy",
  "/security",
  "/accessibility",
];

const dismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
};
const remember = () => {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    /* private mode: the popup simply may return next navigation */
  }
};

const bannerOpen = () =>
  document.documentElement.classList.contains("cookie-banner-open");
const voiceActive = () => !!document.querySelector(".voiceai-orb-btn.active");

export function BookingPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<Element | null>(null);
  const blocked = BLOCKED.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );

  const close = useCallback(() => {
    setOpen(false);
    remember();
  }, []);

  // Arm the timer only on a desktop pointer, once per session, and only after
  // the visitor has answered the cookie banner.
  useEffect(() => {
    if (open || blocked || dismissed()) return;
    if (
      typeof window === "undefined" ||
      !window.matchMedia("(min-width: 768px) and (pointer: fine)").matches
    )
      return;

    let timer = 0;
    let retry = 0;
    const tryOpen = () => {
      if (dismissed()) return;
      // A live voice call owns the screen; wait and check again.
      if (voiceActive()) {
        retry = window.setTimeout(tryOpen, 5000);
        return;
      }
      setOpen(true);
    };
    const start = () => {
      if (!timer) timer = window.setTimeout(tryOpen, OPEN_DELAY);
    };

    let watcher: MutationObserver | undefined;
    if (bannerOpen()) {
      watcher = new MutationObserver(() => {
        if (!bannerOpen()) {
          watcher?.disconnect();
          start();
        }
      });
      watcher.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });
    } else {
      start();
    }

    return () => {
      watcher?.disconnect();
      window.clearTimeout(timer);
      window.clearTimeout(retry);
    };
  }, [open, blocked]);

  // Focus handling, the focus trap, Escape and the scroll lock.
  useEffect(() => {
    if (!open) return;
    opener.current = document.activeElement;
    closeButton.current?.focus();
    const body = document.body;
    const previous = body.style.overflow;
    body.style.overflow = "hidden";

    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab" || !panel.current) return;
      const items = Array.from(
        panel.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null || el === closeButton.current);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      body.style.overflow = previous;
      const back = opener.current;
      if (back instanceof HTMLElement && document.contains(back)) back.focus();
    };
  }, [open, close]);

  useEffect(() => {
    if (open && blocked) setOpen(false);
  }, [open, blocked]);

  if (!open || blocked) return null;

  return (
    <div
      className="rb-bookpop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        className="rb-bookpop-panel"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rb-bookpop-title"
      >
        <button
          className="rb-bookpop-close"
          ref={closeButton}
          onClick={close}
          aria-label="Close the booking invitation"
        >
          <X size={20} />
        </button>
        <div className="rb-bookpop-head">
          <span className="rb-eyebrow">Free 30-minute call</span>
          <h2 id="rb-bookpop-title">Before you go, grab a demo slot.</h2>
          <p>
            Pick a time and we will walk through your calls, your calendar and
            what Chleb would answer for you.
          </p>
        </div>
        <div className="rb-bookpop-body">
          <Suspense
            fallback={<p className="rb-bookpop-loading">Loading the calendar</p>}
          >
            <BrandedScheduler instanceId="popup" />
          </Suspense>
        </div>
      </div>
    </div>
  );
}

export default BookingPopup;
